// Phase 14 — Smart Money Signals Tab (Phase A skeleton).
//
// Three subtabs:
//   1. Today's Shortlist (Phase B populates)
//   2. Signal History   (Phase B populates)
//   3. Data & Signal Status  <-- LIVE in Phase A
//
// The status subtab is fully wired now — it drives the admin operator's
// visibility into ingestion progress, missing dates, import batches, and
// the historical folder scanner button.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../ui/tabs';
import {
  AlertTriangle, CheckCircle2, Clock, Database, FolderInput,
  PlayCircle, RefreshCw, XCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- Small formatter helpers -----------------------------------------------
const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
const badgeForStatus = (s) => {
  if (s === 'ok' || s === 'success') return <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-500/40">{s}</Badge>;
  if (s === 'failed' || s === 'crashed') return <Badge className="bg-red-600/20 text-red-300 border-red-500/40">{s}</Badge>;
  if (s === 'skipped_already_ok' || s === 'skipped_duplicate') return <Badge className="bg-zinc-600/20 text-zinc-300 border-zinc-500/40">{s}</Badge>;
  if (s === 'started') return <Badge className="bg-amber-600/20 text-amber-300 border-amber-500/40">{s}</Badge>;
  return <Badge variant="outline">{s || '—'}</Badge>;
};

// ============================================================================
// DATA & SIGNAL STATUS SUBTAB
// ============================================================================
const DataStatusSubtab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [manualDate, setManualDate] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/phase14/admin/status?days_back=90`, {
        headers: authHeaders(),
      });
      setStatus(r.data);
    } catch (e) {
      console.error('phase14 status fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  const runScan = async (dryRun) => {
    setScanBusy(true);
    setScanResult(null);
    try {
      const r = await axios.post(
        `${API}/api/phase14/admin/scan-history?dry_run=${dryRun ? 'true' : 'false'}`,
        {}, { headers: authHeaders() },
      );
      setScanResult(r.data);
      await refresh();
    } catch (e) {
      setScanResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setScanBusy(false);
    }
  };

  const runManualDate = async () => {
    if (!manualDate) return;
    setManualBusy(true);
    setManualResult(null);
    try {
      const r = await axios.post(
        `${API}/api/phase14/admin/ingest-date`,
        { target_date: manualDate, force: true },
        { headers: authHeaders() },
      );
      setManualResult(r.data);
      await refresh();
    } catch (e) {
      setManualResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setManualBusy(false);
    }
  };

  if (!status && loading) return <div className="text-muted-foreground p-8">Loading…</div>;
  if (!status) return null;

  const c = status.collections || {};
  const m = status.missing_recent || {};

  return (
    <div className="space-y-6" data-testid="phase14-data-status">
      {/* Header actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Data &amp; Signal Status</h2>
          <p className="text-sm text-muted-foreground">
            Phase 14 ingestion foundation. Inbox: <span className="font-mono">{status.inbox_dir}</span>
          </p>
        </div>
        <Button
          onClick={refresh} disabled={loading} variant="outline" size="sm"
          data-testid="phase14-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Collections grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(c).map(([name, meta]) => (
          <Card key={name} data-testid={`phase14-col-${name}`}>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between">
                <Database className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {meta.latest_date || '—'}
                </span>
              </div>
              <div className="text-2xl font-mono text-foreground mt-2">
                {num(meta.documents ?? meta.rows)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {name.replace('phase14_', '').replace(/_/g, ' ')}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Mount-status warning (Phase A safety net) */}
      {status.mount_status && !status.mount_status.is_mount_point && (
        <Alert
          className="border-red-500/40 bg-red-500/10"
          data-testid="phase14-mount-warning"
        >
          <AlertTriangle className="w-4 h-4 text-red-300" />
          <AlertDescription>
            <b>Warning:</b> <span className="font-mono">{status.mount_status.base_dir}</span>{' '}
            is <b>not a host bind mount</b>. Files uploaded via WinSCP to the host will not
            be visible to the container, and any files inside the container will disappear
            on next container recreation. Add a bind mount for this path in your Docker
            Compose file before uploading production data.
          </AlertDescription>
        </Alert>
      )}

      {/* Missing dates */}
      <Card>
        <CardHeader><CardTitle className="text-base">Missing Recent Trading Dates (last 14 shown)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {['equity', 'futures', 'sector'].map((k) => (
              <div key={k}>
                <div className="font-semibold text-foreground mb-1 uppercase text-xs">
                  {k} — {m[k]?.count || 0} missing
                </div>
                {(m[k]?.dates || []).length === 0 ? (
                  <div className="text-emerald-300 flex items-center gap-1 text-xs">
                    <CheckCircle2 className="w-3 h-3" /> up to date
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {m[k].dates.map((d) => (
                      <span
                        key={d}
                        className="text-xs font-mono bg-red-500/10 border border-red-500/30 text-red-300 rounded px-1.5 py-0.5"
                      >{d}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Historical folder scan */}
      <Card>
        <CardHeader><CardTitle className="text-base">Historical ZIP Import</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Alert className="border-amber-500/30 bg-amber-500/10">
            <FolderInput className="w-4 h-4 text-amber-300" />
            <AlertDescription className="text-xs">
              Drop ZIP files into <span className="font-mono text-amber-200">{status.inbox_dir}</span>{' '}
              on the droplet (via WinSCP / scp / rsync), then click <b>Scan Now</b>. Duplicate
              files (same SHA-256) are skipped automatically. Successful imports move to{' '}
              <span className="font-mono">{status.processed_dir}</span>; failures to{' '}
              <span className="font-mono">{status.failed_dir}</span>.
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button
              onClick={() => runScan(true)} disabled={scanBusy} variant="outline" size="sm"
              data-testid="phase14-scan-dryrun-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Dry Run (enumerate + hash only)
            </Button>
            <Button
              onClick={() => runScan(false)} disabled={scanBusy} size="sm"
              data-testid="phase14-scan-run-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              {scanBusy ? 'Scanning…' : 'Scan Now'}
            </Button>
          </div>
          {scanResult && (
            <pre className="text-xs bg-zinc-900/50 p-3 rounded overflow-auto max-h-80 font-mono">
              {JSON.stringify(scanResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      {/* Manual date ingest */}
      <Card>
        <CardHeader><CardTitle className="text-base">Manual Date Ingest</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-40">
              <label className="text-xs text-muted-foreground">Trading date (YYYY-MM-DD)</label>
              <Input
                type="date" value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
                data-testid="phase14-manual-date-input"
              />
            </div>
            <Button
              onClick={runManualDate} disabled={!manualDate || manualBusy} size="sm"
              data-testid="phase14-manual-run-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              {manualBusy ? 'Running…' : 'Run Stages 8–10 (force)'}
            </Button>
          </div>
          {manualResult && (
            <pre className="text-xs bg-zinc-900/50 p-3 rounded overflow-auto max-h-80 font-mono">
              {JSON.stringify(manualResult, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      {/* Recent import batches */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recent Import Batches</CardTitle></CardHeader>
        <CardContent>
          {(status.recent_import_batches || []).length === 0 ? (
            <div className="text-muted-foreground text-sm">No imports yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left border-b border-border">
                  <tr>
                    <th className="p-2">File</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Rows read</th>
                    <th className="p-2">Inserted</th>
                    <th className="p-2">Modified</th>
                    <th className="p-2">Rejected</th>
                    <th className="p-2">Elapsed</th>
                    <th className="p-2">SHA-256</th>
                  </tr>
                </thead>
                <tbody>
                  {status.recent_import_batches.map((b) => (
                    <tr key={b._id} className="border-b border-border/50">
                      <td className="p-2 font-mono">{b.file}</td>
                      <td className="p-2">{badgeForStatus(b.status)}</td>
                      <td className="p-2">{num(b?.totals?.read)}</td>
                      <td className="p-2">{num(b?.totals?.inserted)}</td>
                      <td className="p-2">{num(b?.totals?.modified)}</td>
                      <td className="p-2">{num(b?.totals?.rejected)}</td>
                      <td className="p-2">{b.elapsed_s ? `${b.elapsed_s}s` : '—'}</td>
                      <td className="p-2 font-mono text-muted-foreground">{(b.file_sha256 || '').slice(0, 12)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent ingestion status */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recent Ingestion Status (per-stage, per-date)</CardTitle></CardHeader>
        <CardContent>
          {(status.recent_ingestion_status || []).length === 0 ? (
            <div className="text-muted-foreground text-sm">No ingestion runs yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left border-b border-border">
                  <tr>
                    <th className="p-2">Date</th>
                    <th className="p-2">Stage</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Rows</th>
                    <th className="p-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {status.recent_ingestion_status.map((r) => (
                    <tr key={r._id} className="border-b border-border/50">
                      <td className="p-2 font-mono">{r.target_date}</td>
                      <td className="p-2">{r.stage}</td>
                      <td className="p-2">{badgeForStatus(r.status)}</td>
                      <td className="p-2">{num(r.rows || r.symbols)}</td>
                      <td className="p-2 font-mono text-muted-foreground">
                        {r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// PLACEHOLDER SUBTABS — Phase B populates
// ============================================================================
const TodaysShortlistPlaceholder = () => (
  <Alert className="border-zinc-500/30 bg-zinc-500/5" data-testid="phase14-shortlist-placeholder">
    <Clock className="w-4 h-4 text-muted-foreground" />
    <AlertDescription>
      <b>Phase B pending.</b> The DUAL_ALIGN &amp; SECTOR_TOPQ shortlist appears here once
      the Smart Money signal generator is enabled (next development phase). Data ingestion
      foundation (Phase A) must be validated first.
    </AlertDescription>
  </Alert>
);

const SignalHistoryPlaceholder = () => (
  <Alert className="border-zinc-500/30 bg-zinc-500/5" data-testid="phase14-history-placeholder">
    <Clock className="w-4 h-4 text-muted-foreground" />
    <AlertDescription>
      <b>Phase B pending.</b> Rolling signal history — enabled after Phase B ships.
    </AlertDescription>
  </Alert>
);

// ============================================================================
// TAB ROOT
// ============================================================================
const SmartMoneySignalsTab = () => {
  return (
    <div className="p-6" data-testid="phase14-tab-root">
      <div className="mb-4">
        <h1 className="text-2xl font-heading text-foreground">Smart Money Signals</h1>
        <p className="text-sm text-muted-foreground">
          Phase 14 — 3-consecutive-Long-Buildup × top-quartile sector strength.{' '}
          <span className="text-amber-400">Phase A active (ingestion foundation).</span>{' '}
          Phase B enables live signals.
        </p>
      </div>

      <Tabs defaultValue="status">
        <TabsList data-testid="phase14-subtabs">
          <TabsTrigger value="shortlist" data-testid="phase14-tab-shortlist">
            Today&apos;s Shortlist
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="phase14-tab-history">
            Signal History
          </TabsTrigger>
          <TabsTrigger value="status" data-testid="phase14-tab-status">
            Data &amp; Signal Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="shortlist" className="mt-4">
          <TodaysShortlistPlaceholder />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <SignalHistoryPlaceholder />
        </TabsContent>
        <TabsContent value="status" className="mt-4">
          <DataStatusSubtab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SmartMoneySignalsTab;
